import { Input, Icon, Button } from "@legal-500/legal500-design-system";
import { useEffect, useState, useRef } from "react";
import { featuresService } from "../../api/features.service";
import styles from "./MetadataQuantitiesConfig.module.css";

export type MetadataQuantitiesConfigProps = {
  featureCodes: string[];
  metadata: Record<string, number> | null;
  onChange: (metadata: Record<string, number> | null) => void;
  disabled?: boolean;
  submitAttemptCount?: number;
  onSaveAnyway?: () => void;
  onMissingFieldsChange?: (count: number) => void;
};

type FieldInfo = {
  label: string;
  help_text: string;
};

export function MetadataQuantitiesConfig({
  featureCodes,
  metadata,
  onChange,
  disabled = false,
  submitAttemptCount = 0,
  onSaveAnyway,
  onMissingFieldsChange,
}: MetadataQuantitiesConfigProps) {
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [fieldMetadata, setFieldMetadata] = useState<Record<string, FieldInfo>>({});
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [acknowledgedWarning, setAcknowledgedWarning] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    let hideLoadingTimer: NodeJS.Timeout | null = null;

    // Reset warning state when features change
    setAttemptedSave(false);
    setAcknowledgedWarning(false);

    // Increment request ID to track this specific request
    const currentRequestId = ++requestIdRef.current;

    // Show loading immediately (outside debounce for instant feedback)
    if (featureCodes.length > 0) {
      setShowLoading(true);
    } else {
      setShowLoading(false);
    }

    const timer = setTimeout(async () => {
      if (featureCodes.length === 0) {
        setRequiredFields([]);
        setFieldMetadata({});
        setError(null);
        return;
      }

      try {
        const result = await featuresService.analyzeFeatures(featureCodes);

        // Ignore stale responses - only process if this is still the latest request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setRequiredFields(result.required_entity_fields);
        setFieldMetadata(result.field_metadata);
        setError(null);

        if (metadata) {
          const newMetadata: Record<string, number> = {};
          for (const field of result.required_entity_fields) {
            if (metadata[field] !== undefined) {
              newMetadata[field] = metadata[field];
            }
          }

          if (Object.keys(newMetadata).length > 0) {
            onChange(newMetadata);
          } else if (result.required_entity_fields.length === 0) {
            onChange(null);
          }
        }
      } catch (err) {
        // Ignore errors from stale requests
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const message = err instanceof Error ? err.message : "Failed to analyze features";
        setError(message);
      } finally {
        // Only update loading state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          // Debounce hiding loading indicator (minimum 200ms display time)
          hideLoadingTimer = setTimeout(() => {
            setShowLoading(false);
          }, 200);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (hideLoadingTimer) clearTimeout(hideLoadingTimer);
    };
  }, [featureCodes]);

  // When parent increments submitAttemptCount (on form submit), set attemptedSave
  useEffect(() => {
    if (submitAttemptCount > 0) {
      setAttemptedSave(true);
    }
  }, [submitAttemptCount]);

  // Calculate warning state
  const missingFields = requiredFields.filter(field => !metadata?.[field]);
  const shouldShowWarning = attemptedSave && missingFields.length > 0 && !acknowledgedWarning;

  // Notify parent of missing fields count
  useEffect(() => {
    onMissingFieldsChange?.(missingFields.length);
  }, [missingFields.length, onMissingFieldsChange]);

  const handleSaveAnyway = () => {
    setAcknowledgedWarning(true);
    onSaveAnyway?.();
  };

  const handleFieldChange = (field: string, value: string) => {
    const numValue = value === "" ? null : Number.parseInt(value, 10);

    if (numValue !== null && (Number.isNaN(numValue) || numValue < 1)) {
      return;
    }

    const newMetadata = { ...(metadata || {}) };

    if (numValue === null) {
      delete newMetadata[field];
    } else {
      newMetadata[field] = numValue;
    }

    onChange(Object.keys(newMetadata).length > 0 ? newMetadata : null);
  };

  const handleRetry = () => {
    setError(null);
    const event = new Event("retry");
    window.dispatchEvent(event);
  };

  if (featureCodes.length === 0) {
    return (
      <section className={styles.container}>
        <h3 className={styles.sectionTitle}>Metadata Configuration</h3>
        <p className={styles.emptyMessage}>Select features to configure entity metadata</p>
      </section>
    );
  }

  if (showLoading) {
    return (
      <section className={styles.container}>
        <h3 className={styles.sectionTitle}>Metadata Configuration</h3>
        <div className={styles.loading}>
          <span className={styles.spinner} />
          <span>Analyzing selected features...</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.container}>
        <h3 className={styles.sectionTitle}>Metadata Configuration</h3>
        <div className={styles.error}>
          <div className={styles.errorIcon}>
            <Icon icon="exclamation" size="default" />
          </div>
          <div>
            <p className={styles.errorMessage}>{error}</p>
            <button type="button" onClick={handleRetry} className={styles.retryButton}>
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (requiredFields.length === 0) {
    return (
      <section className={styles.container}>
        <h3 className={styles.sectionTitle}>Metadata Configuration</h3>
        <p className={styles.emptyMessage}>
          No entity configuration required for selected features
        </p>
      </section>
    );
  }

  return (
    <section className={styles.container}>
      <h3 className={styles.sectionTitle}>Metadata Configuration</h3>
      <p className={styles.description}>
        Configure the maximum number of entities for scoped features
      </p>

      <div className={styles.fieldsContainer}>
        {requiredFields.map(field => (
          <div key={field} className={styles.fieldGroup}>
            <Input
              type="number"
              label={fieldMetadata[field]?.label || field}
              helperText={fieldMetadata[field]?.help_text || ""}
              value={metadata?.[field]?.toString() || ""}
              onChange={e => handleFieldChange(field, e.target.value)}
              disabled={disabled}
              min={1}
              placeholder="Enter quantity"
              fullWidth
            />
          </div>
        ))}
      </div>

      {shouldShowWarning && (
        <div className={styles.warning}>
          <p className={styles.warningMessage}>
            The following fields are recommended but not filled:
          </p>
          <ul className={styles.warningList}>
            {missingFields.map(field => (
              <li key={field}>{fieldMetadata[field]?.label || field}</li>
            ))}
          </ul>
          <p className={styles.warningNote}>
            You can still save, but metadata may be needed during subscription activation.
          </p>
          <Button
            color="warning"
            onClick={handleSaveAnyway}
            size="small"
            className={styles.saveAnywayButton}
          >
            Save Anyway
          </Button>
        </div>
      )}
    </section>
  );
}
